import { useEffect, useState } from 'react';

/** Fixed desktop sidebar width (matches Sidebar aside). */
export const SIDEBAR_DESKTOP_WIDTH_PX = 80;

const STORAGE_KEY = 'explore-sidebar-collapsed-desktop-v1';

const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

/** @returns {boolean} true = sidebar hidden on desktop */
export function loadSidebarCollapsedDesktop() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return false;
    return raw === '1' || raw === 'true';
  } catch {
    return false;
  }
}

/** @param {boolean} collapsed */
export function setSidebarCollapsedDesktop(collapsed) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
  emit();
}

/** @param {() => void} cb */
export function subscribeSidebarLayout(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * @returns {[boolean, (collapsed: boolean) => void]}
 */
export function useSidebarCollapsedDesktop() {
  const [collapsed, setCollapsed] = useState(() => loadSidebarCollapsedDesktop());
  useEffect(() => subscribeSidebarLayout(() => setCollapsed(loadSidebarCollapsedDesktop())), []);
  return [collapsed, setSidebarCollapsedDesktop];
}

/**
 * Left inset for fixed UI on desktop when the rail is visible.
 * @returns {{ isDesktop: boolean, insetLeftPx: number, collapsed: boolean }}
 */
export function useSidebarInset() {
  const [collapsed] = useSidebarCollapsedDesktop();
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 769px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const insetLeftPx = isDesktop && !collapsed ? SIDEBAR_DESKTOP_WIDTH_PX : 0;
  return { isDesktop, collapsed, insetLeftPx };
}
